import {
  Button,
  InputGroup,
  InputSelect,
  NAME_REGEX,
  NO_REGEX,
  SWIFT_CODE_REGEX,
  color,
} from "@funded-here-interface/common";
import { FC, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useStyle } from "../../styles/BankDetailsCss";
import { useSelector } from "react-redux";
import { RootState } from "@funded-here-interface/investor-onboarding/src/store";
import { useDispatch } from "react-redux";
import {
  setBankNameRedux,
  setAccountNoRedux,
  setSwiftCodeReduct,
  setAccountTypeRedux,
  setAccountHolderNameRedux,
  setBankAddressRedux,
  setBankCountryRedux,
  setCurrencyRedux,
} from "../../features/indinvestor/bankSliceInd";
import { bankAccountTypes } from "../../constants/bank";
import BankAccNameDisclaimer from "../../components/BankAccNameDisclaimer/BankAccNameDisclaimer";
import { toast } from "react-toastify";
import useGetOrgDraft from "../../hooks/useGetOrgDraft";
import useSaveOrgDraft from "../../hooks/useSaveOrgDraft";
import {
  countries,
  AccountCurrencyTypeEnum,
} from "@funded-here-interface/common/src/constant/country";

const currencyOptions = Object.values(AccountCurrencyTypeEnum);

const BankDetailsInd: FC = () => {
  const { classes } = useStyle();
  const Navigate = useNavigate();
  const dispatch = useDispatch();
  const { userFirstName, userLastName } = useSelector(
    (state: RootState) => state.auth
  );

  const userId = useSelector((state: RootState) => state.auth.userId);
  const { token } = useSelector((state: RootState) => state.auth);

  const getOrgDraftMutation = useGetOrgDraft();
  const saveOrgDraftMutation = useSaveOrgDraft();

  const [initPage, setInitPage] = useState(true);

  const fetchDraft = async (userId: number) => {
    try {
      const params = {
        userId: userId,
        token: token,
      };
      const result = await getOrgDraftMutation.mutateAsync(params);
      return result;
    } catch (error) {
      console.error("Error fetching draft:", error);
    }
  };

  useEffect(() => {
    const initializeDraft = async () => {
      if (initPage) {
        try {
          const data = await fetchDraft(parseInt(userId));

          if (data) {
            setBankName(data.bankName || "");
            setAccountType(data.accountType || "");
            setAccountNumber(data.accountNumber || "");
            setAccountHolderName(data.accountHolderName || "");
            setSwiftCode(data.swiftCode || "");
            setBankAddress(data.bankAddress || "");
            setBankCountry(data.bankCountry || "");
            setCurrency(data.currency || "");
          }
        } catch (error) {
          toast.error(`Error fetching draft: ${error}`);
        } finally {
          setInitPage(false);
        }
      }
    };

    initializeDraft();
  }, [initPage, userId]);

  const [bankName, setBankName] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [accountType, setAccountType] = useState("");
  const [swiftCode, setSwiftCode] = useState("");
  const [accountHolderName, setAccountHolderName] = useState("");
  const [bankAddress, setBankAddress] = useState("");
  const [bankCountry, setBankCountry] = useState("");
  const [currency, setCurrency] = useState("");

  const handleBack = () => {
    saveDraft(false);

    Navigate("/ind-biz-profile");
  };

  const [error, setError] = useState({
    bank_name: "",
    bankAccountNo: "",
    account_type: "",
    swift_code: "",
    accountType: "",
    accountHolderName: "",
    bank_address: "",
    bank_country: "",
    currency: "",
  });

  const handleNext = () => {
    saveDraft(false);
    const customErr = { ...error };
    let valid = true;

    if (!bankName) {
      valid = false;
      customErr.bank_name = "Bank Name is required";
    }
    if (!swiftCode) {
      valid = false;
      customErr.swift_code = "Swift Code is required";
    }
    if (!accountNumber) {
      valid = false;
      customErr.bankAccountNo = "Bank Account no is required";
    }

    if (!accountType) {
      valid = false;
      customErr.accountType = "Account Type is required";
    }

    if (!bankAddress) {
      valid = false;
      customErr.bank_address = "Bank Address is required";
    }

    if (!bankCountry) {
      valid = false;
      customErr.bank_country = "Bank Country is required";
    }

    if (!currency) {
      valid = false;
      customErr.currency = "Currency is required";
    }

    if (!accountHolderName) {
      valid = false;
      customErr.accountHolderName = "Account Holder Name is required";
    } else if (userFirstName && userLastName) {
      const userRegisteredName = `${userFirstName.toLowerCase()} ${userLastName.toLowerCase()}`;

      if (accountHolderName.toLowerCase() !== userRegisteredName) {
        valid = false;
        customErr.accountHolderName =
          "Account holder name must match your name registered on our platform";
        toast.warn(
          "Account holder name must match your name registered on our platform"
        );
      }
    }

    if (valid) {
      // Dispatch bank details to Redux
      dispatch(setBankNameRedux(bankName));
      dispatch(setAccountNoRedux(accountNumber));
      dispatch(setAccountTypeRedux(accountType));
      dispatch(setSwiftCodeReduct(swiftCode));
      dispatch(setAccountHolderNameRedux(accountHolderName));
      dispatch(setBankAddressRedux(bankAddress));
      dispatch(setBankCountryRedux(bankCountry));
      dispatch(setCurrencyRedux(currency));

      setTimeout(() => {
        Navigate("/doc-upload-ind");
      }, 500);
    } else {
      setError(customErr);
    }
  };

  const saveDraft = (showToast: Boolean = true) => {
    saveOrgDraftMutation.mutateAsync(
      {
        userId: parseInt(userId),
        token: token,
        bankName: bankName,
        accountNumber: accountNumber,
        accountHolderName: accountHolderName,
        accountType: accountType,
        swiftCode: swiftCode,
        bankAddress: bankAddress,
        bankCountry: bankCountry,
        currency: currency,
      },
      {
        onSuccess: () => {
          if (showToast) {
            toast.success("Draft Saved");
          }
        },
        onError: (e) => {
          toast.error((e as Error).message);
        },
      }
    );
  };

  return (
    <div style={{ marginTop: "70px" }}>
      <BankAccNameDisclaimer isIndividual />
      <InputGroup
        isErrored={error.accountHolderName}
        errorMessage
        onChange={(value: string) => {
          error.accountHolderName = "";
          setAccountHolderName(value);
        }}
        labelText="Account Holder Name"
        value={accountHolderName}
        id="account_holder_name"
      />
      <InputGroup
        isErrored={error.bank_name}
        errorMessage
        onChange={(value: string) => {
          error.bank_name = "";
          setBankName(value.replace(NAME_REGEX, ""));
        }}
        labelText="Bank Name"
        id="bank_name"
        value={bankName}
      />

      <InputGroup
        isErrored={error.bankAccountNo}
        errorMessage
        onChange={(value: string) => {
          error.bankAccountNo = "";
          setAccountNumber(value.replace(NO_REGEX, ""));
        }}
        labelText="Bank Account Number"
        id="account_number"
        value={accountNumber}
      />
      <InputSelect
        isErrored={error?.accountType}
        errorMessage
        onChange={(value: string) => {
          error.accountType = "";
          setAccountType(value);
        }}
        labelText="Bank Account Type"
        id="accountType"
        value={accountType}
        data={bankAccountTypes}
      />

      <InputGroup
        isErrored={error.swift_code}
        errorMessage
        onChange={(value: string) => {
          error.swift_code = "";
          setSwiftCode(value.replace(SWIFT_CODE_REGEX, ""));
        }}
        labelText="Bank SWIFT Code"
        value={swiftCode}
        id="swift_code"
      />
      <InputGroup
        isErrored={error.bank_address}
        errorMessage
        onChange={(value: string) => {
          error.bank_address = "";
          setBankAddress(value);
        }}
        labelText="Bank Address"
        value={bankAddress}
        id="bank_address"
      />
      <InputSelect
        isErrored={error.bank_country}
        errorMessage
        onChange={(value: string) => {
          error.bank_country = "";
          setBankCountry(value);
        }}
        labelText="Bank Country"
        id="bank_country"
        value={bankCountry}
        data={countries}
      />
      <InputSelect
        isErrored={error.currency}
        errorMessage
        onChange={(value: string) => {
          error.currency = "";
          setCurrency(value);
        }}
        labelText="Currency"
        id="currency"
        value={currency}
        data={currencyOptions}
      />

      <div className={classes.buttonDiv}>
        <div className={classes.buttonWrapper}>
          <Button
            onClick={handleBack}
            backgroundColor={color.WHITE}
            textColor={color.FHGREEN}
            children="Back"
            border="1px solid #338F86"
            width="100%"
          />
        </div>
        <div className={classes.buttonWrapper}>
          <Button
            onClick={handleNext}
            textColor={color.WHITE}
            backgroundColor={color.FHGREEN}
            children="Next"
            width="100%"
          />
        </div>
      </div>
      <div className={classes.buttonWrapper}>
        <Button
          onClick={saveDraft}
          textColor={color.WHITE}
          backgroundColor={color.LOANTEXT}
          children="Save Draft"
          width="100%"
        />
      </div>
    </div>
  );
};

export default BankDetailsInd;
